import React, { useCallback, useRef, useState } from "react";

import monitorApi from "../../api/monitorApi";
import Header from "../../components/Header/Header";
import { UploadStateType } from "./UploadDnsCsvPageTypes";

function UploadDnsCsvPage() {
  const [file, setFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadStateType>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorDetails, setErrorDetails] = useState<
    { row: number; errors: string[] }[]
  >([]);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setFile(null);
    setUploadState("idle");
    setErrorMessage("");
    setErrorDetails([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = (f: File | undefined) => {
    if (!f) return;
    if (!f.name.endsWith(".csv")) {
      setUploadState("error");
      setErrorMessage("Only .csv files are accepted.");
      setErrorDetails([]);
      return;
    }
    setFile(f);
    setUploadState("idle");
    setErrorMessage("");
    setErrorDetails([]);
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0]);
  };

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  }, []);

  const upload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadState("uploading");
    setErrorMessage("");
    setErrorDetails([]);

    try {
      await monitorApi.uploadDnsCheckCsv(formData);
      setUploadState("success");
    } catch (err: any) {
      setUploadState("error");
      const data = err.response?.data;
      if (data?.error) {
        setErrorMessage(data.error);
      } else {
        setErrorMessage(err.message || "Upload failed.");
      }
      if (data?.details) {
        setErrorDetails(data.details);
      }
    }
  };

  if (uploadState === "success") {
    return (
      <main className="w-full">
        <Header />
        <div className="max-w-xl mx-auto mt-20 text-center p-4">
          <div className="rounded-lg border border-green-300 bg-green-50 p-10">
            <svg
              className="mx-auto mb-4 h-16 w-16 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <h2 className="text-2xl font-semibold text-green-800 mb-2">
              Upload successful
            </h2>
            <p className="text-green-700 mb-6">
              The CSV file has been processed.
            </p>
            <button
              onClick={resetForm}
              className="px-6 py-2 rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              Upload another file
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full">
      <Header />
      <div className="max-w-xl mx-auto mt-10 px-4 pb-8">
        <h1 className="text-2xl font-semibold mb-6">Upload DNS Check CSV</h1>

        {/* Drag-and-drop zone */}
        <div
          data-testid="dns-csv-drop-zone"
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`
            flex flex-col items-center justify-center
            border-2 border-dashed rounded-lg p-10 mb-2 cursor-pointer
            transition-colors
            ${
              dragActive
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
            }
          `}
        >
          {file ? (
            <>
              <svg
                className="h-10 w-10 text-blue-500 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
              <p className="text-blue-700 font-medium truncate max-w-full">
                {file.name}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  resetForm();
                }}
                className="mt-2 text-sm text-gray-500 hover:text-red-500 transition-colors"
              >
                Remove
              </button>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-1">
                Drag and drop a <span className="font-medium">.csv</span> file
                here
              </p>
              <p className="text-sm text-gray-400">or click to browse</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            onChange={onFileSelect}
            className="hidden"
            data-testid="dns-csv-file-input"
          />
        </div>

        {/* Error display */}
        {uploadState === "error" && (
          <div className="bg-red-50 border border-red-300 rounded p-4 mb-4">
            <p className="text-red-800 font-medium">{errorMessage}</p>
            {errorDetails.length > 0 && (
              <div className="mt-2 max-h-48 overflow-y-auto text-sm text-red-700">
                {errorDetails.map((d, i) => (
                  <div key={i} className="mb-1">
                    <span className="font-medium">Row {d.row}:</span>{" "}
                    {d.errors.join(" ")}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upload button */}
        <button
          onClick={upload}
          disabled={uploadState === "uploading"}
          className={`
            w-full py-2.5 rounded font-medium transition-colors
            ${
              !file || uploadState === "uploading"
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }
          `}
        >
          {uploadState === "uploading" ? "Uploading..." : "Upload"}
        </button>
      </div>
    </main>
  );
}

export default UploadDnsCsvPage;
